import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO = '/logo.png';

const NAV_LINKS = [
  { href: '#hero',      label: 'Home',      id: 'hero' },
  { href: '#services',  label: 'Services',  id: 'services' },
  { href: '#about',     label: 'About',     id: 'about' },
  { href: '#process',   label: 'Process',   id: 'process' },
  { href: '#peb',       label: 'PEB',       id: 'peb' },
  { href: '#portfolio', label: 'Portfolio', id: 'portfolio' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('hero');
  const location = useLocation();
  const isPortfolioPage = location.pathname.includes('/portfolio');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      if (isPortfolioPage) return;
      
      const sections = NAV_LINKS.map(l => l.id).concat(['contact']);
      let current = 'hero';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) current = id;
      }
      setActive(current);
    };

    if (isPortfolioPage) {
      setActive('portfolio');
      const handleScrollOnly = () => setScrolled(window.scrollY > 60);
      window.addEventListener('scroll', handleScrollOnly, { passive: true });
      return () => window.removeEventListener('scroll', handleScrollOnly);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return () => window.removeEventListener('scroll', onScroll);
    }
  }, [isPortfolioPage]);

  const handleLinkClick = () => setOpen(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-[#4A4A4A] ${
        scrolled ? 'bg-[#0A0A0A]/97 backdrop-blur-md' : 'bg-[#0A0A0A]/85 backdrop-blur-sm'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo & Desktop Links Group */}
          <div className="flex items-center gap-12 lg:gap-16">
            {/* Logo */}
            <a href={isPortfolioPage ? '/#hero' : '#hero'} onClick={handleLinkClick} data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
              <img src={LOGO} alt="Kadam Patil Projects" className="h-14 lg:h-16 w-auto object-contain" />
            </a>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-7">
              {NAV_LINKS.map(({ href, label, id }) => (
                <a
                  key={id}
                  href={isPortfolioPage && href.startsWith('#') ? `/${href}` : href}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={`font-body text-xs font-medium tracking-[0.18em] uppercase transition-colors duration-200 ${
                    active === id ? 'text-[#C41E3A]' : 'text-[#D1D5DB] hover:text-white'
                  }`}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Desktop CTA + Mobile Toggle Button */}
          <div className="flex items-center gap-4">
            <a
              href={isPortfolioPage ? '/#contact' : '#contact'}
              data-testid="nav-cta-contact"
              className="hidden lg:inline-block bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors duration-200"
            >
              GET IN TOUCH
            </a>
            
            {/* Hamburger icon for mobile viewports */}
            <button
              className="lg:hidden text-white p-2 focus:outline-none hover:text-[#C41E3A] transition-colors"
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Menu overlay */}
      {open && (
        <div className="lg:hidden bg-[#0A0A0A] border-t border-[#4A4A4A] absolute top-20 left-0 right-0 z-40 transition-all duration-300">
          <div className="px-6 py-6 space-y-2">
            {NAV_LINKS.map(({ href, label, id }) => (
              <a
                key={id}
                href={isPortfolioPage && href.startsWith('#') ? `/${href}` : href}
                onClick={handleLinkClick}
                data-testid={`nav-mobile-${label.toLowerCase()}`}
                className={`block font-body text-sm font-medium uppercase tracking-widest py-3.5 border-b border-[#1A1A1A] transition-colors ${
                  active === id ? 'text-[#C41E3A]' : 'text-white hover:text-[#C41E3A]'
                }`}
              >
                {label}
              </a>
            ))}
            <a
              href={isPortfolioPage ? '/#contact' : '#contact'}
              onClick={handleLinkClick}
              className="block mt-6 bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-widest px-6 py-4 text-center clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors duration-200"
              data-testid="nav-mobile-cta"
            >
              GET IN TOUCH
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
